import { XMLParser } from 'fast-xml-parser';

import { type InvertedLocalize } from '../../../shared/utils/localization';
import { type SamlProviderMetadataFileProperties } from '../types';

function getFirst<T>(item: T | T[] | undefined): T | undefined {
  if (Array.isArray(item)) {
    return item[0];
  }

  return item;
}

interface ExpectedData {
  EntityDescriptor?: {
    IDPSSODescriptor?: {
      NameIDFormat?: string | string[];
      SingleLogoutService?: {
        '@_Location'?: string;
      };
      '@_WantAuthnRequestsSigned'?: boolean;
    };
  };
}

interface ParseError {
  isError: true;
  message: string;
}

/** Parses a SAML metadata file */
export function samlMetadataParser(
  fileContents: string,
  l: InvertedLocalize
): SamlProviderMetadataFileProperties | ParseError {
  try {
    const parser = new XMLParser({
      removeNSPrefix: true,
      ignoreAttributes: false,
      attributeNamePrefix: '@_',
      parseAttributeValue: true,
      processEntities: false,
      trimValues: true,
    });

    const parsed = parser.parse(fileContents) as ExpectedData;
    const entityDescriptor = parsed.EntityDescriptor;

    if (!entityDescriptor) {
      return {
        isError: true,
        message: l('No EntityDescriptor found in file.'),
      };
    }

    const idpDescriptor = entityDescriptor.IDPSSODescriptor;

    if (!idpDescriptor) {
      return {
        isError: true,
        message: l('No IDPSSODescriptor found in file.'),
      };
    }

    // Extract NameIDFormat - handle both single value and array (optional)
    const nameIdFormat = getFirst(idpDescriptor.NameIDFormat) ?? '';

    const wantsAuthRequestsSigned =
      idpDescriptor['@_WantAuthnRequestsSigned'] === true;

    // Extract logout service URL from SingleLogoutService (optional)
    const logoutServiceUrl =
      getFirst(idpDescriptor.SingleLogoutService)?.['@_Location'] ?? '';

    return {
      protocol: 'saml',
      logoutServiceUrl,
      wantsAuthRequestsSigned,
      nameIDPolicyFormat: nameIdFormat,
    };
  } catch (e) {
    return {
      isError: true,
      message: l('Error parsing file: {error}', {
        error: (e as Error).message,
      }),
    };
  }
}
